<template>
  <section id="location" class="visit-section">
    <h2 class="section-title">{{ $t('visit.title') }}</h2>

    <div class="visit-layout">
      <!-- Map (GDPR consent placeholder until the visitor opts in) -->
      <div class="visit-map">
        <div v-if="!businessAddress" class="map-skeleton">
          <p>{{ $t('visit.loadingMap') }}</p>
        </div>
        <PrivacyMap v-else :mapUrl="googleMapsEmbedUrl" height="380px" />
      </div>

      <!-- Contact + hours -->
      <div class="visit-info">
        <div v-if="businessAddress" class="visit-block">
          <h3 class="visit-block-title">{{ $t('footer.address') }}</h3>
          <p class="visit-address selectable">{{ businessAddress }}</p>
          <a
            class="visit-link"
            :href="mapsSearchUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ $t('visit.openInMaps') }}
          </a>
        </div>

        <div v-if="businessPhone || businessEmail" class="visit-block">
          <a v-if="businessPhone" class="visit-link visit-contact" :href="telHref">
            <span class="visit-contact-label">{{ $t('visit.call') }}</span>
            <span class="selectable">{{ businessPhone }}</span>
          </a>
          <a v-if="businessEmail" class="visit-link visit-contact" :href="`mailto:${businessEmail}`">
            <span class="visit-contact-label">{{ $t('footer.email') }}</span>
            <span class="selectable">{{ businessEmail }}</span>
          </a>
        </div>

        <div class="visit-block">
          <h3 class="visit-block-title">{{ $t('footer.openingHours') }}</h3>
          <ul class="visit-hours">
            <li
              v-for="row in hourRows"
              :key="row.key"
              class="visit-hours-row"
              :class="{ 'is-today': row.isToday }"
            >
              <span class="visit-day">
                {{ $t(`days.${row.key}`) }}
                <span v-if="row.isToday" class="visit-today-tag">{{ $t('visit.today') }}</span>
              </span>
              <span v-if="row.open" class="visit-time">{{ row.open }}–{{ row.close }}</span>
              <span v-else class="visit-time is-closed">{{ $t('common.closed') }}</span>
            </li>
          </ul>
        </div>

        <button
          type="button"
          class="visit-cta"
          @pointerdown="prefetchBookingSheet"
          @click="openBookingSheet({ source: 'visit' })"
        >
          {{ $t('services.bookNow') }}
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import { computed, onMounted } from 'vue'
import { useBusinessSettings } from '@/composables/useBusinessSettings'
import { useBookingSheet } from '@/composables/useBookingSheet'
import PrivacyMap from './PrivacyMap.vue'

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

export default {
  name: 'VisitUsSection',
  components: { PrivacyMap },
  setup() {
    const {
      businessAddress,
      businessEmail,
      businessPhone,
      openingHours,
      todayKey,
      load: loadBusinessSettings
    } = useBusinessSettings()
    const { open: openBookingSheet, prefetch: prefetchBookingSheet } = useBookingSheet()

    const googleMapsEmbedUrl = computed(
      () => `https://www.google.com/maps?q=${encodeURIComponent(businessAddress.value)}&output=embed`
    )

    const mapsSearchUrl = computed(
      () =>
        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(businessAddress.value)}`
    )

    // tel: accepts digits and a single leading + only
    const telHref = computed(() => {
      const raw = String(businessPhone.value || '')
      const plus = raw.trim().startsWith('+') ? '+' : ''
      return `tel:${plus}${raw.replace(/\D/g, '')}`
    })

    const hourRows = computed(() =>
      DAY_ORDER.map((key) => {
        const day = openingHours.value?.[key]
        const isOpen = Boolean(day && day.open && day.close)
        return {
          key,
          open: isOpen ? day.open : null,
          close: isOpen ? day.close : null,
          isToday: key === todayKey.value
        }
      })
    )

    onMounted(() => {
      loadBusinessSettings()
    })

    return {
      businessAddress,
      businessEmail,
      businessPhone,
      googleMapsEmbedUrl,
      mapsSearchUrl,
      telHref,
      hourRows,
      openBookingSheet,
      prefetchBookingSheet
    }
  }
}
</script>

<style scoped>
.visit-section {
  padding: clamp(48px, 8vw, 64px) 0;
}

.section-title {
  width: min(100% - 2 * var(--gutter), 1200px);
  margin: 0 auto var(--s-6);
  text-align: center;
  font-size: var(--fs-h2);
  color: var(--color-heading);
  text-shadow: 1px 1px 3px var(--text-shadow-color);
}

.visit-layout {
  width: min(100% - 2 * var(--gutter), 1200px);
  margin: 0 auto;
  display: grid;
  gap: var(--s-5);
}

.visit-map {
  border-radius: var(--r-3);
  overflow: hidden;
  border: 1px solid var(--border-light);
  box-shadow: var(--shadow-secondary);
}

.map-skeleton {
  height: 380px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface-muted);
  color: var(--text-muted);
}

.visit-info {
  display: flex;
  flex-direction: column;
  gap: var(--s-5);
  padding: var(--s-5);
  background: var(--surface-card);
  border: 1px solid var(--border-light);
  border-radius: var(--r-3);
  box-shadow: var(--shadow-secondary);
}

.visit-block {
  display: flex;
  flex-direction: column;
  gap: var(--s-2);
}

.visit-block-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-strong);
}

.visit-address {
  margin: 0;
  color: var(--text-primary);
  font-size: var(--fs-body);
  line-height: 1.5;
}

.selectable {
  user-select: text;
  -webkit-user-select: text;
}

.visit-link {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: var(--s-2);
  min-height: 44px;
  padding: var(--s-2) 0;
  color: var(--color-primary);
  font-weight: 700;
  text-decoration: underline;
}

.visit-contact {
  gap: var(--s-3);
  text-decoration: none;
}

.visit-contact .selectable {
  text-decoration: underline;
}

.visit-contact-label {
  color: var(--text-muted);
  font-weight: 600;
  min-width: 56px;
}

.visit-link:focus-visible,
.visit-cta:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

.visit-hours {
  list-style: none;
  margin: 0;
  padding: 0;
}

.visit-hours-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: var(--s-4);
  padding: 5px 0;
  color: var(--text-secondary);
  font-size: 0.95rem;
}

.visit-hours-row.is-today {
  color: var(--text-strong);
  font-weight: 700;
}

.visit-today-tag {
  display: inline-block;
  margin-left: var(--s-2);
  padding: 1px 8px;
  border-radius: 20px;
  background: var(--color-primary-soft);
  color: var(--text-primary);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  vertical-align: middle;
}

.visit-time {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.visit-time.is-closed {
  color: var(--color-closed);
  font-style: italic;
}

.visit-cta {
  min-height: 48px;
  padding: 0 var(--s-5);
  border: 2px solid var(--color-primary);
  border-radius: 50px;
  background: transparent;
  color: var(--text-primary);
  font-family: inherit;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s ease, color 0.15s ease;
}

.visit-cta:hover,
.visit-cta:active {
  background: var(--gradient-brand);
  color: var(--text-on-gradient);
  border-color: transparent;
}

@media (min-width: 1024px) {
  .visit-layout {
    grid-template-columns: 7fr 5fr;
    align-items: start;
  }
}
</style>
